import { store } from '@/store'
import { NavigationGuardNext, Route } from 'vue-router'

/**
 * SubscriptionGuard
 * 
 * Check if the user has an active or trialing subscription.
 */
const SubscriptionGuard = (to: Route, from: Route, next: NavigationGuardNext) => {

    //  If the user doesn't exist route to home page
    if (!store.getters.subscription) {
        store.dispatch('subscriptionGet').then(subscription => {
            if (!subscription) {
                store.getters.appDebug && console.log('GUARD: subscription-guard stop.')
                return next('/')
            }
            proceed()
        })
    } else {
        proceed()
    }

    function proceed() {
        const subscription = store.getters.subscription
        const isAdmin = store.getters.authIsAdmin
        const isApp = store.getters.isApp

        //  Check if the user is a MessageDesk support user
        const isSupport = store.getters.isSupportUser(store.getters.user)

        //  Check if the subscription has an active status
        // const isActiveStatus = subscription.status === 'trialing' || subscription.status === 'active'
        const isActiveStatus = subscription.status !== 'canceled'

        //  Check if the subscription is an expired trial
        //  This is no longer applicable because an expired trial will now put you into a past due state 
        // const isExpiredTrial = subscription.cancel_at_period_end === true

        /**
         * NOTE: We use 'cancel_at_period_end' to determine if the subscription is an expired
         * trial.  Our trialing plan does not cost any money, so Stripe sets the subscription
         * status to 'active' when the trial ends.  To get around this, we handle Stripes
         * 'TrialExpired' webhook by setting the subscription to 'cancel at the end' of your
         * billing period.
         */

        //  The guard passes if the user isSupport OR if they are in an active state (anything but canceled)
        const pass = isSupport || isActiveStatus

        //  If the guard passes...
        if (pass) {
            //   Log the successful pass
            store.getters.appDebug && console.log(`GUARD: subscription-guard pass...`)

            //  Continue
            return next()
        }

        //  Else, log the failure
        store.getters.appDebug && console.log('GUARD: subscription-guard stop.', {isSupport, isActiveStatus})

        //  Define a message selector
        const messages = {
            //  Message for non-admin users
            nonAdmin: 'Please contact your admin about entering their payment info to reactivate your account and access MessageDesk.',
            //  Message for native app users.  We let them know they can't access subscriptions on the mobile app
            isApp: 'The MessageDesk mobile app does not allow managing subscriptions or payments.',
            //  Message for admin users
            isAdmin: 'Please choose a subscription plan to reactivate your account and access MessageDesk.'
        }

        //  Select the correct message
        const subTxt = isApp ? messages['isApp'] : isAdmin ? messages['isAdmin'] : messages['nonAdmin']

        //  Pop the message dialog
        store.dispatch('dialogOpen', {
            type: 'MESSAGE',
            title: 'Subscription Past Due',
            text: `Your subscription status is canceled, or your trial has ended, so we have restricted your access to just this page. ${subTxt}`
        })

        //  If on the mobile app, route to memberships, else route to subscription management
        let route = isApp ? '/memberships' : '/billing/subscription'

        //  If we are already at billing subscription, do nothing
        if (from.path.includes('/billing')) return

        //  Else, go to selected route
        return next(route)
    }
}

export default SubscriptionGuard